import WebSocketManager from './websocket-manager';
import type { WebSocketManagerConfig } from './types';
import { apidata } from '../../out/prices';
import { WebsocketClientType } from '../constants/common';

export type PriceCallback = (message: apidata.prices.PriceData) => void;

interface ISubscriptions {
  path: string;
  callback: PriceCallback;
  subId: string;
}

// Default protobuf decoder for prices
const defaultProtobufDecoder = (data: Uint8Array): apidata.prices.PriceData => {
  const PriceDataClass = apidata.prices.PriceData;
  return PriceDataClass.decode(data) as unknown as apidata.prices.PriceData;
};

export interface PricesWebSocketManagerConfig {
  websocketUrl: string;
  socketId: WebsocketClientType;
  protobufDecoder?: (data: Uint8Array) => apidata.prices.PriceData;
  websocketConfig: WebSocketManagerConfig;
}

class PricesWebSocketManager extends WebSocketManager {
  private static pricesInstance: PricesWebSocketManager | null = null;
  private pricesWebSocketUrl: string;
  private static setConnectionCallback: (
    ws: WebSocket | null,
    client: string,
  ) => void = () => {};
  private socketId: WebsocketClientType = WebsocketClientType.PRICES;
  private subscriptions: Map<string, ISubscriptions[]> = new Map();
  private connectionStartTime: number = 0;
  private isConnectionInProgress: boolean = false;
  private lastOnMessageError: string = '';
  private static natsConnectionStatus: string = 'closed';
  private protobufDecoder: (data: Uint8Array) => apidata.prices.PriceData;

  private constructor(config: PricesWebSocketManagerConfig) {
    super(config.websocketConfig);
    this.pricesWebSocketUrl = config.websocketUrl;
    this.socketId = config.socketId;
    this.protobufDecoder = config.protobufDecoder || defaultProtobufDecoder;
  }

  static setNatsConnection(status: string) {
    this.natsConnectionStatus = status;
  }

  static getPricesInstance(
    config?: PricesWebSocketManagerConfig,
  ): PricesWebSocketManager {
    if (!this.pricesInstance) {
      if (!config) {
        throw new Error('Config is required for first initialization');
      }
      this.pricesInstance = new PricesWebSocketManager(config);
    }
    return this.pricesInstance;
  }

  static initializePricesSocketManager(
    config: PricesWebSocketManagerConfig,
    setConnectionCallback: (ws: WebSocket | null, client: string) => void,
    hashToken: string | null = null,
  ): void {
    console.log('[PricesWebSocketManager] Initializing socket manager', {
      socketId: config.socketId,
      hasHashToken: !!hashToken,
    });
    if (!this.pricesInstance) {
      this.pricesInstance = new PricesWebSocketManager(config);
    }
    super.initialize(hashToken);
    PricesWebSocketManager.setConnectionCallback = setConnectionCallback;
  }

  static getPricesWSInstance(): PricesWebSocketManager | null {
    if (!this.pricesInstance) {
      return null;
    }
    return this.pricesInstance;
  }

  async initializePriceConnection(): Promise<void> {
    try {
      if (this.isConnectionInProgress) {
        console.log('connection already in progress---');
        return;
      }
      this.isConnectionInProgress = true;
      console.log('[PricesWebSocketManager] Initializing connection', {
        socketId: this.socketId,
        url: this.pricesWebSocketUrl,
      });
      await super.initializeConnection(
        this.socketId,
        this.pricesWebSocketUrl,
        this.setupEventListeners.bind(this),
      );
      this.connectionStartTime = Date.now();
    } catch (error: unknown) {
      console.error(
        '[PricesWebSocketManager] Error initializing price connection:',
        error,
      );
    } finally {
      this.isConnectionInProgress = false;
    }
  }

  async disconnectPriceConnection(): Promise<void> {
    void super.disconnect(this.socketId);
  }

  getPricesConnection(): WebSocket | null {
    return super.getSocket(this.socketId);
  }

  private setupEventListeners() {
    const socket = this.getPricesConnection();

    if (!socket) {
      return;
    }

    socket.onopen = () => {
      console.log(`WebSocket connected --> ${this.socketId}`);

      // Ensure socket is in the static Map (may have been cleared by cleanup/HMR)
      super.ensureSocketInMap(this.socketId, socket);

      PricesWebSocketManager.setConnectionCallback(socket, this.socketId);

      super.resetReconnectAttempts(this.socketId);

      const connectionTime = super.getConnectionCreationTime(this.socketId);
    };

    socket.onclose = (event) => {
      console.log(`WebSocket disconnected --> ${this.socketId}`, event);

      PricesWebSocketManager.setConnectionCallback(null, this.socketId);

      if (!event.wasClean) {
        super.handleReconnection(this.socketId, 'onclose');
      }
    };

    socket.onmessage = async (event: MessageEvent) => {
      try {
        const arrayBuffer = await (event.data instanceof Blob
          ? event.data.arrayBuffer()
          : event.data);

        const uint8Array = new Uint8Array(arrayBuffer);
        const decodedMessage = this.protobufDecoder(uint8Array);

        this.subscriptions.forEach((subscriptions, path) => {
          const subPath = decodedMessage.subPath;

          if (!subPath) {
            console.error('No subPath found in the message', decodedMessage);
            return;
          }

          if (path === subPath) {
            subscriptions.forEach((sub) => {
              try {
                sub.callback(decodedMessage);
              } catch (error: unknown) {
                // Error can be send to the callback as well, but not sending in this case
                console.error('Error processing WebSocket message', error);
              }
            });
          }
        });

        this.lastOnMessageError = '';
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        console.error(
          `Error processing WebSocket message --> ${this.socketId}`,
          errorMessage,
        );
        if (this.lastOnMessageError !== errorMessage) {
          this.lastOnMessageError = errorMessage;
        }
      }
    };

    socket.onerror = (error) => {
      console.error(`WebSocket error --> ${this.socketId}`, error);

      super.handleReconnection(this.socketId, 'onerror');
    };
  }

  subscribe(path: string, callback: PriceCallback): () => void {
    try {
      const subId = `sub-${path}-prices-${Math.random()
        .toString(8)
        .slice(2, 11)}`;
      const socket = this.getPricesConnection();
      if (!socket) {
        console.log('Prices connection not initialized');
        throw new Error('Prices connection not initialized');
      }

      const existingSubscriptions = this.subscriptions.get(path) ?? [];

      if (!existingSubscriptions.length) {
        if (socket.readyState == WebSocket.OPEN) {
          const subscribeMessage = {
            action: 'SUB',
            mode: 'P',
            entities: [`${path}`],
          };
          socket.send(JSON.stringify(subscribeMessage));
        }
      }

      this.subscriptions.set(path, [
        ...existingSubscriptions,
        { path, callback, subId },
      ]);

      // Return unsubscribe function
      return () => {
        const subs = this.subscriptions.get(path);
        if (subs) {
          const filteredSubs = subs.filter((sub) => sub.subId !== subId);

          if (filteredSubs.length === 0) {
            if (socket.readyState === WebSocket.OPEN) {
              const unsubscribeMessage = {
                action: 'UNSUB',
                mode: 'P',
                entities: [`${path}`],
              };
              socket.send(JSON.stringify(unsubscribeMessage));
            }
            this.subscriptions.delete(path);
          } else {
            this.subscriptions.set(path, filteredSubs);
          }
        }
      };
    } catch (err: unknown) {
      console.error('Error subscribing to price', err);

      return () => {};
    }
  }

  isSubscribed(path: string): boolean {
    return this.subscriptions.has(path);
  }
}

export default PricesWebSocketManager;
